/**
 * Prediction Markets API - Singleton wrapper around blockchain providers.
 *
 * **Architecture:**
 * - Singleton pattern enforced (use exported functions, never instantiate providers directly)
 * - Two providers: EvmPredictionMarketProvider (blockchain) + PlayPredictionMarketProvider (demo)
 * - Automatic batching via MarketBatcher (10ms window)
 * - Auto-switching between providers based on account type
 *
 * **Provider Selection:**
 * - EVM accounts → EvmPredictionMarketProvider (real blockchain transactions)
 * - Play accounts → PlayPredictionMarketProvider (local storage, no gas fees)
 *
 * **Key Features:**
 * - Market CRUD: create, fetch, settle, void
 * - Predictions: place, fetch history
 * - Creator management: register, update, fetch stats
 * - Event subscriptions (WebSocket + HTTP polling)
 * - Cache management with TTL (market: 5min, creator: 5min, config: ∞)
 * - Automatic request deduplication
 *
 * **Usage Guidelines:**
 * ⚠️ NEVER instantiate providers directly - use exported functions
 * ⚠️ For direct provider access (advanced): use `evmProvider.instance`
 * ✅ All exported functions are provider-agnostic (handle EVM/Play switching)
 *
 * **Performance:**
 * - Request batching: Multiple concurrent getMarketById() → single RPC call
 * - Cache: Reduces RPC calls by ~90% for repeated queries
 * - Multicall3: Batch contract reads (up to 100x faster than sequential)
 * - Provider selection: WebSocket for events (instant), HTTP for bulk queries
 *
 * @module predictionMarkets
 * @example
 * ```typescript
 * // Market queries
 * const market = await getMarketById('123');
 * const markets = await getMarketsByIds(['1', '2', '3']); // Auto-batched
 *
 * // Transactions
 * await createMarket(...);
 * await makePrediction('123', '0', '100', userAddress);
 * await pickWinner('123', '1', 'proof url');
 *
 * // Creator management
 * const creator = await getCreatorDetails(address);
 * const stats = await getCreatorCounts(address);
 *
 * // Event subscriptions
 * await subscribeToMarketUpdates((market) => {
 *   console.log('Market updated:', market);
 * });
 *
 * // Cache management
 * clearMarketCache(['123'], 'manual refresh');
 * warmCache(); // On app startup
 * ```
 */

import {
  type Market,
  type OutcomeInput,
  type CreatorDetails,
  type MarketListener,
  type CreatorCounts,
  MarketType,
  type Prediction,
} from '@/common/predictionMarkets/types.ts';
import PlayPredictionMarketProvider from './predictionMarketProviders/PlayPredictionMarketProvider.ts';
import type EvmPredictionMarketProvider from '@/common/predictionMarkets/predictionMarketProviders/EvmPredictionMarketProvider';
import { MarketBatcher } from '@/common/predictionMarkets/marketBatcher';
import type { Listener } from 'ethers';
import { PLAY_ACCOUNT_ADDRESS } from '@/composables/accounts/providers/playAccountProvider.ts';

/**
 * Statistics for a creator (volume, earnings, settled markets, etc.)
 * Used by batch fetching functions for efficient multi-creator queries
 */
type CreatorStats = {
  totalVolume: number;
  totalEarnings: number;
  totalSettledMarkets: number;
  totalPredictionsCount: number;
  lastActivityTimestamp: number;
};

// Lazy singleton pattern to avoid circular dependency during module initialization
let predictionMarketProviderSingletonEvm: EvmPredictionMarketProvider | null = null;
let predictionMarketProviderSingletonPlay: PlayPredictionMarketProvider | null = null;
// Cache the initialization promise to prevent race conditions when multiple concurrent calls happen
let evmProviderInitPromise: Promise<EvmPredictionMarketProvider> | null = null;

/**
 * Get or create the EVM provider singleton
 * Lazy-loads the EvmPredictionMarketProvider class (~18.3 KiB + 57 KiB ABI) on first use
 * This keeps non-blockchain pages (UserGuide, etc.) lightweight
 * Handles concurrent calls correctly by caching the initialization promise
 * @returns Promise resolving to singleton instance of EvmPredictionMarketProvider
 */
async function getEvmProvider(): Promise<EvmPredictionMarketProvider> {
  if (predictionMarketProviderSingletonEvm) {
    return predictionMarketProviderSingletonEvm;
  }

  // If initialization is already in progress, return the existing promise
  if (evmProviderInitPromise) {
    return evmProviderInitPromise;
  }

  // Start initialization and cache the promise
  evmProviderInitPromise = (async () => {
    const { default: EvmPredictionMarketProvider } = await import(
      '@/common/predictionMarkets/predictionMarketProviders/EvmPredictionMarketProvider'
    );
    predictionMarketProviderSingletonEvm = new EvmPredictionMarketProvider();
    return predictionMarketProviderSingletonEvm;
  })();

  return evmProviderInitPromise;
}

/**
 * Get or create the Play provider singleton
 * Used for demo/play account functionality without real blockchain transactions
 * @returns Singleton instance of PlayPredictionMarketProvider
 */
function getPlayProvider(): PlayPredictionMarketProvider {
  if (!predictionMarketProviderSingletonPlay) {
    predictionMarketProviderSingletonPlay = new PlayPredictionMarketProvider();
  }
  return predictionMarketProviderSingletonPlay;
}

/**
 * Singleton getter for direct access to EVM provider methods
 * ⚠️ WARNING: Do NOT create new EvmPredictionMarketProvider instances!
 * Multiple instances create duplicate WebSocket connections and waste resources
 * Use this getter or the exported wrapper functions below
 * @returns Promise resolving to EvmPredictionMarketProvider instance
 */
export const evmProvider = {
  get instance() {
    return getEvmProvider(); // Now returns Promise<EvmPredictionMarketProvider>
  },
};

/**
 * Market batcher singleton - automatically batches market requests within a 10ms window
 * Prevents "100 separate calls for 1 market" anti-pattern by combining concurrent requests
 * Used internally by getMarketsByIds() for efficiency
 */
const marketBatcher = new MarketBatcher(
  async (ids) => {
    const provider = await getEvmProvider();
    return provider.getMarketsByIds(ids);
  },
  10, // 10ms batch window
);

/**
 * Get total number of registered creators on the platform
 * @returns Total creator count
 */
export async function getCreatorsCount(): Promise<number> {
  const provider = await getEvmProvider();
  return await provider.getCreatorsCount();
}

/**
 * Get total number of markets created on the platform
 * @returns Total market count
 */
export async function getMarketsCount(): Promise<number> {
  const provider = await getEvmProvider();
  return await provider.getMarketsCount();
}

/**
 * Get total number of predictions placed on the platform
 * @returns Total prediction count
 */
export async function getPredictionsCount(): Promise<number> {
  const provider = await getEvmProvider();
  return await provider.getPredictionsCount();
}

/**
 * Get total number of markets created by a specific creator
 * @param creatorAddress Creator's wallet address
 * @returns Total market count for this creator
 */
export async function getCreatorMarketsCount(creatorAddress: string): Promise<number> {
  const provider = await getEvmProvider();
  return await provider.getCreatorMarketsCount(creatorAddress);
}

/**
 * Get number of live (active, before deadline) markets for a specific creator
 * @param creatorAddress Creator's wallet address
 * @returns Live market count for this creator
 */
export async function getCreatorLiveMarketsCount(creatorAddress: string): Promise<number> {
  const provider = await getEvmProvider();
  return await provider.getCreatorActiveMarketsCount(creatorAddress, true);
}

/**
 * Get number of pending (past deadline, awaiting settlement) markets for a specific creator
 * @param creatorAddress Creator's wallet address
 * @returns Pending market count for this creator
 */
export async function getCreatorPendingMarketsCount(creatorAddress: string): Promise<number> {
  const provider = await getEvmProvider();
  return await provider.getCreatorActiveMarketsCount(creatorAddress, false);
}

/**
 * Get number of settled markets for a specific creator
 * @param creatorAddress Creator's wallet address
 * @returns Settled market count for this creator
 */
export async function getCreatorSettledMarketsCount(creatorAddress: string): Promise<number> {
  const provider = await getEvmProvider();
  return await provider.getCreatorSettledMarketsCount(creatorAddress);
}

/**
 * Get all market counts for a creator (all/live/pending/settled) in one RPC call
 * More efficient than calling individual count functions
 * @param creatorAddress Creator's wallet address
 * @returns Object with all, live, pending counts
 */
export async function getCreatorCounts(creatorAddress: string): Promise<CreatorCounts> {
  const provider = await getEvmProvider();
  return await provider.getCreatorCounts(creatorAddress);
}

/**
 * Get total number of active (live + pending) markets for a creator
 * @param creatorAddress Creator's wallet address
 * @returns Active market count (live + pending)
 */
export async function getCreatorActiveMarketsCount(creatorAddress: string): Promise<number> {
  const provider = await getEvmProvider();
  return await provider.getCreatorActiveMarketsCount(creatorAddress);
}

/**
 * Batch fetch active market counts for multiple creators in one RPC call
 * Uses multicall for efficiency when fetching many creators
 * @param creatorAddresses Array of creator wallet addresses
 * @returns Record mapping creator address to active market count
 */
export async function getCreatorActiveMarketsCountBatch(creatorAddresses: string[]): Promise<Record<string, number>> {
  const provider = await getEvmProvider();
  return await provider.getCreatorActiveMarketsCountBatch(creatorAddresses);
}

/**
 * Batch fetch creator statistics (volume, earnings, settled markets, etc.) for multiple creators
 * Uses multicall for efficiency when fetching many creators
 * @param creatorAddresses Array of creator wallet addresses
 * @returns Record mapping creator address to their stats
 */
export async function getCreatorStatsBatch(creatorAddresses: string[]): Promise<Record<string, CreatorStats>> {
  const provider = await getEvmProvider();
  return await provider.getCreatorStatsBatch(creatorAddresses);
}

/**
 * Get paginated list of markets created by a specific creator
 * @param creatorAddress Creator's wallet address
 * @param page Page number (1-indexed, default: 1)
 * @returns Array of markets for this page
 */
export async function getMarketsByCreatorAddress(creatorAddress: string, page: number = 1): Promise<Market[]> {
  const provider = await getEvmProvider();
  return await provider.getMarketsByCreatorAddress(creatorAddress, page);
}

/**
 * Get markets by IDs with automatic batching
 *
 * Requests made within a 10ms window are automatically batched together
 * into a single underlying RPC call for efficiency.
 *
 * @param marketIds Array of market IDs to fetch
 * @returns Record mapping market IDs to Market objects
 */
export async function getMarketsByIds(marketIds: string[]): Promise<Record<string, Market>> {
  if (marketIds.length === 0) {
    return {};
  }

  // Use the batcher which will automatically combine requests within the batch window
  return await marketBatcher.loadMany(marketIds);
}

/**
 * Get a single market by its ID
 * @param marketId Market ID
 * @returns Market object
 */
export async function getMarketById(marketId: string): Promise<Market> {
  const provider = await getEvmProvider();
  return await provider.getMarketById(marketId);
}

/**
 * Fetch rules for a specific market on-demand (lazy loading)
 * This is called when user clicks "View Rules" to avoid blocking initial market display
 * Includes in-flight tracking to prevent duplicate requests
 */
export async function getMarketRules(marketId: string): Promise<string> {
  const provider = await getEvmProvider();
  return await provider.getMarketRules(marketId);
}

/**
 * Batch fetch rules for multiple markets efficiently
 * Uses optimized blob batching for parallel fetching
 * @param markets Array of markets to fetch rules for
 */
export async function batchFetchMarketRules(markets: Market[]): Promise<void> {
  const provider = await getEvmProvider();
  return await provider.batchFetchMarketRules(markets);
}

/**
 * Get paginated list of all registered creators
 * @param page Page number (1-indexed)
 * @returns Array of creator details for this page
 */
export async function getCreators(page: number): Promise<CreatorDetails[]> {
  const provider = await getEvmProvider();
  return await provider.getCreators(page);
}

/**
 * Settle a market by declaring the winning outcome
 * Only the market creator can call this after the deadline
 * @param predictionId Market ID to settle
 * @param outcomeId ID of the winning outcome
 * @param proof Optional proof/evidence for settlement (not currently used)
 * @returns Promise<true> when settlement transaction succeeds
 */
export async function pickWinner(predictionId: string, outcomeId: string, proof?: string): Promise<boolean> {
  const provider = await getEvmProvider();
  await provider.settleMarket(predictionId, outcomeId, proof);
  return true;
}

/**
 * Create a new prediction market
 * @param title Market title/question
 * @param marketType Type of market (e.g., BINARY, MULTIPLE_CHOICE)
 * @param outcomes Array of possible outcomes
 * @param endUnixTime Market deadline (Unix timestamp in seconds)
 * @param rules Market rules text (stored on-chain as blob)
 * @param feeBps Creator fee in basis points (e.g., 500 = 5%)
 * @param minPredictionAmount Minimum prediction amount in wei
 * @returns The newly created market ID
 */
export async function createMarket(
  title: string,
  marketType: MarketType,
  outcomes: OutcomeInput[],
  endUnixTime: number,
  rules: string,
  feeBps: number,
  minPredictionAmount: bigint,
): Promise<number> {
  const provider = await getEvmProvider();
  return await provider.createMarket(title, marketType, outcomes, endUnixTime, rules, feeBps, minPredictionAmount);
}

/**
 * Cancel/void a market, refunding all predictions
 * Only the market creator can call this before settlement
 * @param predictionId Market ID to void
 * @returns Promise<true> when void transaction succeeds
 */
export async function voidMarket(predictionId: string): Promise<boolean> {
  const provider = await getEvmProvider();
  await provider.voidMarket(predictionId);
  return true;
}

/**
 * Approve the prediction market contract to spend tokens on behalf of the user
 * Required before placing predictions
 * @param amount Amount to approve (in token's base units)
 * @param userId User's wallet address
 */
export async function approveToken(amount: string, userId: string): Promise<void> {
  const provider = await getEvmProvider();
  await provider.approveToken(amount, userId);
}

/**
 * Place a prediction on a market outcome
 * Automatically handles play account vs real wallet routing
 * @param market Market object to predict on
 * @param outcomeId ID of the outcome to predict on
 * @param amount Amount to bet (in token's base units)
 * @param userId User's wallet address (or PLAY_ACCOUNT_ADDRESS)
 * @param approvalAmount Optional: specific approval amount if different from bet amount
 * @param minShares Optional: minimum shares expected (for slippage protection, not currently used)
 * @returns Promise<true> when prediction transaction succeeds
 */
export async function makePrediction(
  market: Market,
  outcomeId: string,
  amount: string,
  userId: string,
  approvalAmount?: string,
  minShares?: string,
): Promise<boolean> {
  if (userId === PLAY_ACCOUNT_ADDRESS) {
    return await getPlayProvider().makePrediction(market, outcomeId, amount, userId);
  }
  const provider = await getEvmProvider();
  return await provider.makePrediction(market.id, outcomeId, amount, userId, approvalAmount, minShares);
}

/**
 * Get detailed information about a specific creator
 * @param creatorAddress Creator's wallet address
 * @returns Creator details (nickname, ID, description, etc.)
 * @throws UserNotCreatorError if address is not a registered creator
 */
export async function getCreatorDetails(creatorAddress: string): Promise<CreatorDetails> {
  const provider = await getEvmProvider();
  return await provider.getCreatorDetails(creatorAddress);
}

/**
 * Get creator's wallet address from their numeric creator ID
 * @param creatorId Creator's numeric ID (e.g., "1", "2")
 * @returns Creator's wallet address
 */
export async function getCreatorAddressFromCreatorId(creatorId: string): Promise<string> {
  const provider = await getEvmProvider();
  return await provider.getCreatorAddressFromCreatorId(creatorId);
}

/**
 * Subscribe to blockchain events (CreatedMarket, SettledMarket, PlacedPrediction, CancelledMarket)
 * Automatically uses WebSocket for instant updates (<100ms) with HTTP polling fallback
 * @param listener Callback function to invoke when markets are created/updated
 * @returns Listener reference from ethers.js contract (legacy, not used for cleanup)
 */
export async function subscribeToMarketUpdates(listener: MarketListener): Promise<Listener> {
  const provider = await getEvmProvider();
  return await provider.subscribeToMarketUpdates(listener);
}

/**
 * Unsubscribe from all blockchain event updates
 * Removes all event listeners (CreatedMarket, SettledMarket, PlacedPrediction, CancelledMarket)
 * from both HTTP and WebSocket providers
 * @returns Promise that resolves when all listeners are removed
 */
export async function unsubscribeFromMarketUpdates(): Promise<void> {
  const provider = await getEvmProvider();
  await provider.unsubscribeFromMarketUpdates();
}

/**
 * Register current user as a creator on the platform
 * @param nickname Creator's display name
 * @param streamUrl Creator's stream/channel URL
 * @param description Creator bio/description
 * @returns Promise<true> when registration transaction succeeds
 */
export async function registerUserAsCreator(nickname: string, streamUrl: string, description: string): Promise<boolean> {
  const provider = await getEvmProvider();
  return await provider.registerUserAsCreator(nickname, streamUrl, description);
}

/**
 * Update creator profile information
 * Only callable by registered creators
 * @param nickname Updated display name
 * @param streamUrl Updated stream/channel URL
 * @param description Updated bio/description
 * @returns Promise<true> when update transaction succeeds
 */
export async function updateUserAsCreator(nickname: string, streamUrl: string, description: string): Promise<boolean> {
  const provider = await getEvmProvider();
  return await provider.updateUserAsCreator(nickname, streamUrl, description);
}

/**
 * Fetch user's predictions in a batch (multiple pages at once)
 * Automatically handles play account vs real wallet routing
 * @param userId User's wallet address (or PLAY_ACCOUNT_ADDRESS)
 * @param pages Array of page numbers to fetch (1-indexed)
 * @returns Combined array of predictions across all requested pages
 */
export async function getUserPredictionsBatch(userId: string, pages: number[]): Promise<Prediction[]> {
  if (userId === PLAY_ACCOUNT_ADDRESS) {
    return await getPlayProvider().getUserPredictions();
  }
  const provider = await getEvmProvider();
  return await provider.getUserPredictionsBatch(userId, pages);
}

/**
 * Get total number of predictions placed by a user
 * Automatically handles play account vs real wallet routing
 * @param userId User's wallet address (or PLAY_ACCOUNT_ADDRESS)
 * @returns Total prediction count for this user
 */
export async function getUserPredictionsCount(userId: string): Promise<number> {
  if (userId === PLAY_ACCOUNT_ADDRESS) {
    return await getPlayProvider().getUserPredictionsCount();
  }

  const provider = await getEvmProvider();
  return await provider.getUserPredictionsCount(userId);
}

/**
 * Get platform fee percentage charged on predictions
 * @returns Platform fee in basis points (e.g., 200 = 2%)
 */
export async function getPlatformFees(): Promise<bigint> {
  const provider = await getEvmProvider();
  return await provider.getPlatformFees();
}

/**
 * Get minimum creator fee percentage allowed when creating markets
 * @returns Minimum fee in basis points (e.g., 100 = 1%)
 */
export async function getMinCreatorFees(): Promise<number> {
  const provider = await getEvmProvider();
  return await provider.getMinCreatorFees();
}

/**
 * Get maximum creator fee percentage allowed when creating markets
 * @returns Maximum fee in basis points (e.g., 1000 = 10%)
 */
export async function getMaxCreatorFees(): Promise<number> {
  const provider = await getEvmProvider();
  return await provider.getMaxCreatorFees();
}

/**
 * Get minimum prediction amount allowed by the platform
 * @returns Minimum amount in wei (token base units)
 */
export async function getMinPredictionAmount(): Promise<bigint> {
  const provider = await getEvmProvider();
  return await provider.getMinPredictionAmount();
}

/**
 * Get the prediction market contract address
 * @returns Contract address (checksummed)
 */
export async function getContractAddress(): Promise<string> {
  return (await getEvmProvider()).MARKET_ADDRESS;
}

/**
 * Get user's token balance, allowance, and native balance in one call
 * Automatically handles play account vs real wallet routing
 * @param userId User's wallet address (or PLAY_ACCOUNT_ADDRESS)
 * @returns Object with balance (tokens), allowance (approved for contract), and nativeBalance (BNB/ETH)
 */
export async function getTokenBalanceAndAllowance(userId: string): Promise<{ balance: bigint; allowance: bigint; nativeBalance: bigint }> {
  if (userId === PLAY_ACCOUNT_ADDRESS) {
    return await getPlayProvider().getTokenBalanceAndAllowance();
  }
  const provider = await getEvmProvider();
  return await provider.getTokenBalanceAndAllowance(userId);
}

/**
 * Clear cached prediction counts
 * @param userId Optional: specific user to clear (clears all if omitted)
 */
export async function clearPredictionCountCache(userId?: string): Promise<void> {
  (await getEvmProvider()).clearPredictionCountCache(userId);
}

/**
 * Clear cached market data
 * @param marketIds Optional: specific market IDs to clear (clears all if omitted)
 * @param source Optional: debug label for logging who triggered the clear
 */
export async function clearMarketCache(marketIds?: string[], source?: string): Promise<void> {
  (await getEvmProvider()).clearMarketCache(marketIds, source);
}

/**
 * Clear cached creator list (paginated homepage results)
 */
export async function clearCreatorCache(): Promise<void> {
  (await getEvmProvider()).clearCreatorCache();
}

/**
 * Clear cached homepage statistics (total creators, markets, predictions)
 */
export async function clearHomePageStatsCache(): Promise<void> {
  (await getEvmProvider()).clearHomePageStatsCache();
}

/**
 * Clear cached active market counts for creators
 * @param creatorAddress Optional: specific creator to clear (clears all if omitted)
 */
export async function clearCreatorActiveMarketsCountCache(creatorAddress?: string): Promise<void> {
  (await getEvmProvider()).clearCreatorActiveMarketsCountCache(creatorAddress);
}

/**
 * Clear cached creator statistics (volume, earnings, etc.)
 * @param creatorAddress Optional: specific creator to clear (clears all if omitted)
 */
export async function clearCreatorStatsCache(creatorAddress?: string): Promise<void> {
  (await getEvmProvider()).clearCreatorStatsCache(creatorAddress);
}

/**
 * Clear cached creator details (nickname, description, etc.)
 * @param creatorAddress Optional: specific creator to clear (clears all if omitted)
 */
export async function clearCreatorDetailsCache(creatorAddress?: string): Promise<void> {
  (await getEvmProvider()).clearCreatorDetailsCache(creatorAddress);
}

/**
 * Clear cached creator ID to address mappings
 * @param creatorId Optional: specific creator ID to clear (clears all if omitted)
 */
export async function clearCreatorAddressByIdCache(creatorId?: string): Promise<void> {
  (await getEvmProvider()).clearCreatorAddressByIdCache(creatorId);
}

/**
 * Clear cached creator market counts (all/live/pending)
 * @param creatorAddress Optional: specific creator to clear (clears all if omitted)
 */
export async function clearCreatorCountsCache(creatorAddress?: string): Promise<void> {
  (await getEvmProvider()).clearCreatorCountsCache(creatorAddress);
}

/**
 * Get all contract configuration parameters in one call
 * @returns Object with max market duration, fee limits, min prediction amount, currency, affiliate fee, odds floor
 */
export async function getContractConfig(): Promise<{
  maxTime: number;
  platformFees: bigint;
  minCreatorFees: number;
  maxCreatorFees: number;
  minPredictionAmount: bigint;
  predictionCurrency: string;
  affiliateFeePercentage: number;
  lowestPossibleOdds: bigint;
}> {
  const provider = await getEvmProvider();
  return await provider.getContractConfig();
}

/**
 * Get the token symbol used for predictions (e.g., "USDC", "USDT")
 * @returns Token symbol
 */
export async function getPredictionCurrencySymbol(): Promise<string> {
  const provider = await getEvmProvider();
  return await provider.getPredictionCurrencySymbol();
}

/**
 * Get the number of decimals for the prediction token
 * @returns Token decimals (e.g., 6 for USDC, 18 for most ERC20s)
 */
export async function getTokenDecimals(): Promise<number> {
  const provider = await getEvmProvider();
  return await provider.getTokenDecimals();
}

/**
 * Get the amount of tokens the contract is approved to spend on behalf of user
 * @param userId User's wallet address
 * @returns Allowance amount in wei (token base units)
 */
export async function getTokenAllowance(userId: string): Promise<bigint> {
  const provider = await getEvmProvider();
  return await provider.getTokenAllowance(userId);
}

/**
 * Get user's token balance
 * @param userId User's wallet address
 * @returns Balance in wei (token base units)
 */
export async function getTokenBalance(userId: string): Promise<bigint> {
  const provider = await getEvmProvider();
  return await provider.getTokenBalance(userId);
}

/**
 * Set the token allowance for the prediction market contract
 * @param amount Allowance amount in wei (token base units)
 * @returns Promise<true> when approval transaction succeeds
 */
export async function setTokenAllowance(amount: bigint): Promise<boolean> {
  const provider = await getEvmProvider();
  return await provider.setTokenAllowance(amount);
}

/**
 * Transfer native currency (BNB/ETH) to another address
 * @param recipientAddress Destination wallet address
 * @param amount Amount to send (in native currency base units)
 * @returns Transaction hash
 */
export async function transferNative(recipientAddress: string, amount: string): Promise<string> {
  const provider = await getEvmProvider();
  return await provider.transferNative(recipientAddress, amount);
}

/**
 * Transfer prediction tokens to another address
 * @param recipientAddress Destination wallet address
 * @param amount Amount to send (in token base units)
 * @returns Transaction hash
 */
export async function transferToken(recipientAddress: string, amount: string): Promise<string> {
  const provider = await getEvmProvider();
  return await provider.transferToken(recipientAddress, amount);
}

/**
 * Pre-warm caches by fetching commonly used data
 * Called on app initialization to improve first-load performance
 */
export async function warmCache(): Promise<void> {
  const provider = await getEvmProvider();
  return await provider.warmCache();
}

// Re-export cache clearing functions from other modules for convenience
export { clearSettlementCache } from './settlement';
export { clearCreatorAddressToDetailsCache } from '@/composables/useCreatorMarkets';
export { clearCreatorCache as clearUseCreatorCache } from '@/composables/useCreator';

/**
 * Get the WebSocket connection quality from the provider
 * Returns 'disconnected' if provider not yet loaded (lazy loading)
 * @returns 'excellent' | 'good' | 'poor' | 'disconnected'
 */
export function getWsConnectionQuality(): 'excellent' | 'good' | 'poor' | 'disconnected' {
  // If provider not yet loaded (lazy loading), return disconnected
  if (!predictionMarketProviderSingletonEvm) {
    return 'disconnected';
  }
  return predictionMarketProviderSingletonEvm.getWsConnectionQuality();
}

/**
 * Check if the provider's WebSocket is subscribed to events
 * Returns false if provider not yet loaded (lazy loading)
 * @returns true if subscribed, false otherwise
 */
export function getWsIsSubscribed(): boolean {
  // If the provider hasn't been loaded yet (lazy loading), return false
  // Once loaded, check if connection quality is not 'disconnected'
  // (the provider subscribes during initialization)
  return getWsConnectionQuality() !== 'disconnected';
}

export default {
  getCreatorsCount,
  getMarketsCount,
  getCreatorMarketsCount,
  getCreatorLiveMarketsCount,
  getCreatorPendingMarketsCount,
  getCreatorSettledMarketsCount,
  getCreatorCounts,
  getCreatorActiveMarketsCount,
  getCreatorActiveMarketsCountBatch,
  getCreatorStatsBatch,
  getMarketsByCreatorAddress,
  getMarketById,
  getCreators,
  pickWinner,
  createMarket,
  voidMarket,
  makePrediction,
  getCreatorDetails,
  getCreatorAddressFromCreatorId,
  subscribeToMarketUpdates,
  unsubscribeFromMarketUpdates,
  registerUserAsCreator,
  updateUserAsCreator,
  getUserPredictionsBatch,
  getUserPredictionsCount,
  clearPredictionCountCache,
  clearMarketCache,
  clearCreatorCache,
  clearHomePageStatsCache,
  clearCreatorActiveMarketsCountCache,
  clearCreatorStatsCache,
  clearCreatorDetailsCache,
  clearCreatorAddressByIdCache,
  getPlatformFees,
  getMinCreatorFees,
  getMaxCreatorFees,
  getMinPredictionAmount,
  getContractConfig,
  getContractAddress,
};
